import { Dispatch } from 'react';
import { MAX_DESCRIPTION_FIELD_LENGTH, MAX_NAME_FIELD_LENGTH } from 'constants/rules';
import { RuleActions } from '../reducers/rule/ruleReducer';
import { RuleEditor, RuleEditorControl } from '../schemas/IterationRule.schema';

type InputWidth = '2' | '3' | '4' | '5' | '10' | '20' | '30' | 2 | 3 | 4 | 5 | 10 | 20 | 30;
export type Part = { Type: 'TEXT' | 'VARIABLE'; Value?: string };

export type RuleEditorFields = {
  control: RuleEditorControl | undefined;
  label: string;
  defaultValue: string;
  errorMessage: string;
  regEx: string;
  placeholderText: string;
  helperText: string;
  width: number | undefined;
  values: { Label: string; Value: string }[];
};

export function getUpdatedTemplate(parts: Part[] = [], variables: string[] = []): string {
  let varIndex = 0;
  const textParts = parts.filter((part) => part.Type === 'TEXT');
  const variableParts = parts.filter((part) => part.Type === 'VARIABLE');

  if (textParts.length === 1 || variableParts.length === 1) {
    return parts
      .map((part) => {
        if (part.Type === 'TEXT') {
          return part.Value;
        }
        if (part.Type === 'VARIABLE') {
          const remainingVars = variables.slice(varIndex);
          const value = remainingVars.length > 1 ? remainingVars.join(', ') : remainingVars[0] ?? '';
          varIndex = variables.length;
          return value;
        }
        return '';
      })
      .join('');
  }

  return parts
    .map((part) => {
      if (part.Type === 'TEXT') {
        return part.Value;
      }
      if (part.Type === 'VARIABLE') {
        const value = variables[varIndex] ?? '';
        varIndex += 1;
        return value;
      }
      return '';
    })
    .join('');
}

export function generateComparator(
  template: Part[] | undefined,
  variables: string[],
): string {
  if (!template) {
    return '';
  }

  const vars = [...variables];

  const textParts = template.filter((part) => part.Type === 'TEXT');

  if (textParts.length === 1 && vars.length > 1) {
    const separator = textParts[0].Value ?? ',';
    return vars.join(separator);
  }

  return template.reduce((acc, part) => {
    if (part.Type === 'TEXT') {
      return acc + (part.Value ?? '');
    }

    if (part.Type === 'VARIABLE') {
      const variableValue = vars.shift() || '';
      return acc + variableValue;
    }
    return acc;
  }, '');
}

export function updateRuleName(
  dispatch: Dispatch<RuleActions>,
  ruleEditor: RuleEditor | undefined,
  variables: string[],
  formattedVariables?: string[],
): void {
  if (!ruleEditor) {
    return;
  }
  const variablesToUse = formattedVariables && formattedVariables.length > 0 ? formattedVariables : variables;
  const name = getUpdatedTemplate(ruleEditor.NameTemplate, variablesToUse);
  dispatch({ type: 'SET_NAME', payload: name.slice(0, MAX_NAME_FIELD_LENGTH) });
}

export function updateRuleDescription(
  dispatch: Dispatch<RuleActions>,
  ruleEditor: RuleEditor | undefined,
  variables: string[],
  formattedVariables?: string[],
): void {
  if (!ruleEditor) {
    return;
  }
  const variablesToUse = formattedVariables && formattedVariables.length > 0 ? formattedVariables : variables;
  const description = getUpdatedTemplate(ruleEditor.DescriptionTemplate, variablesToUse);
  dispatch({ type: 'SET_DESCRIPTION', payload: description.slice(0, MAX_DESCRIPTION_FIELD_LENGTH) });
}

export function updateRuleComparator(
  dispatch: Dispatch<RuleActions>,
  ruleEditor: RuleEditor | undefined,
  comparatorValue: string,
  variables: string[],
  formattedVariables?: string[],
) {
  const rawComparator = ruleEditor?.ComparatorTemplate && variables.length > 0
    ? generateComparator(ruleEditor.ComparatorTemplate, variables)
    : comparatorValue;

  const valueToDispatch = rawComparator.endsWith(',') ? rawComparator.slice(0, -1) : rawComparator;

  dispatch({ type: 'SET_COMPARATOR', payload: valueToDispatch });
  updateRuleName(dispatch, ruleEditor, variables, formattedVariables);
  updateRuleDescription(dispatch, ruleEditor, variables, formattedVariables);
}

export function getWidth(width: number | undefined): InputWidth | undefined {
  const allowed: InputWidth[] = ['2', '3', '4', '5', '10', '20', '30', 2, 3, 4, 5, 10, 20, 30];
  return allowed.includes(width as InputWidth) ? width as InputWidth : undefined;
}

export function getRuleComparatorVariables(
  value: string,
  template: { Type: string; Value?: string | undefined; }[] | undefined,
): string[] {
  if (value === '' || !template || template.length === 0) {
    return [];
  }

  let remaining = value;
  const variables: string[] = [];

  for (let idx = 0; idx < template.length; idx += 1) {
    const part = template[idx];

    if (part.Type === 'TEXT' && part.Value) {
      const nextIdx = remaining.indexOf(part.Value);
      if (nextIdx >= 0) {
        const variableValue = remaining.slice(0, nextIdx);
        if (variableValue !== '') {
          variables.push(variableValue);
        }
        remaining = remaining.slice(nextIdx + part.Value.length);
      } else {
        if (remaining !== '') {
          variables.push(remaining);
        }
        remaining = '';
        break;
      }
    }
  }

  if (remaining !== '') {
    variables.push(remaining);
  }

  return variables;
}

export function isValidInput(value: string, regexString?: string): boolean {
  if (!regexString) {
    return true;
  }
  try {
    const regex = new RegExp(regexString);
    return regex.test(value);
  } catch {
    return false;
  }
}

export function getRuleEditorControl(
  ruleEditor: RuleEditor | undefined,
  controlKey?: number | string,
): RuleEditorFields {
  let control;

  if (typeof controlKey === 'number') {
    control = ruleEditor?.MetaData?.Controls?.[controlKey];
  } else if (typeof controlKey === 'string') {
    control = ruleEditor?.MetaData?.Controls?.find((c) => c.Type === controlKey);
  } else {
    control = ruleEditor?.MetaData?.Controls?.[0];
  }

  if (control === undefined) {
    return {
      control: undefined,
      label: '',
      defaultValue: '',
      errorMessage: '',
      regEx: '',
      placeholderText: '',
      helperText: '',
      width: undefined,
      values: [],
    };
  }

  return {
    control,
    label: control.Label || '',
    defaultValue: control.DefaultValue || '',
    errorMessage: control.Validation?.ErrorMessage || '',
    regEx: control.Validation?.RegEx || '',
    placeholderText: control.PlaceholderText || '',
    helperText: control.HelperText || '',
    width: control.Width || 0,
    values: control.Values || [],
  };
}

export function hasVaccineTargetControl(ruleEditor: RuleEditor | undefined): boolean {
  const vaccineTargetControl = getRuleEditorControl(ruleEditor, 'VACCINE_TARGET');
  return vaccineTargetControl.control !== undefined;
}

export function resolvePlaceHolder(value: string): string {
  switch (value) {
    case '{{today}}': {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }

    default:
      return value;
  }
}

export function isValidDate(day: string, month: string, year: string): boolean {
  if (!day || !month || !year) {
    return false;
  }

  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (Number.isNaN(dayNum) || Number.isNaN(monthNum) || Number.isNaN(yearNum)) {
    return false;
  }

  if (monthNum < 1 || monthNum > 12) {
    return false;
  }

  if (dayNum < 1 || dayNum > 31) {
    return false;
  }

  const date = new Date(yearNum, monthNum - 1, dayNum);

  return (
    date.getDate() === dayNum
    && date.getMonth() === monthNum - 1
    && date.getFullYear() === yearNum
  );
}

const COMPARATOR_ERROR_MESSAGES: Record<string, string> = {
  TEXT: 'Enter a comparator value',
  TEXT_AND_DATE: 'Enter a comparator value and a date.',
  LIST: 'Select a comparator value from the list.',
  RADIOS: 'Select a comparator value.',
  PICK_LIST: 'Select one or more comparator values from the pick list.',
  DEFAULT: "Enter a comparator. For example 'Yes'.",
  DATE: 'Enter a date.',
};

export function getComparatorRequiredError(ruleEditor: RuleEditor | undefined): string {
  if (!ruleEditor?.Type) {
    return COMPARATOR_ERROR_MESSAGES.DEFAULT;
  }

  return COMPARATOR_ERROR_MESSAGES[ruleEditor.Type] ?? COMPARATOR_ERROR_MESSAGES.DEFAULT;
}
